import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook to calculate and manage dropdown menu position 'above' or 'below' input
 * based on available viewport space. Position is calculated once when the menu opens
 * and remains fixed until the menu closes (no repositioning while open).
 *
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.anchorRef - Ref to the anchor element (e.g., input)
 * @param {boolean} options.isOpen - Whether the menu is currently open
 * @param {number} [options.minHeight=120] - Min menu height in pixels
 * @returns {string} - 'above' or 'below' indicating menu position
 */
export const useMenuPosition = ({ anchorRef, isOpen, minHeight = 120 }) => {
  const [menuPosition, setMenuPosition] = useState("below");
  const wasOpenRef = useRef(false);

  const calculatePosition = useCallback(() => {
    if (anchorRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      if (spaceBelow < minHeight && spaceAbove > spaceBelow) {
        setMenuPosition("above");
      } else {
        setMenuPosition("below");
      }
    }
  }, [anchorRef, minHeight]);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;

    // Only calculate position when transitioning from closed to open
    if (isOpen && !wasOpen) {
      calculatePosition();
      return;
    }

    // Reset to below when closing
    if (!isOpen) {
      setMenuPosition("below");
    }
  }, [isOpen, calculatePosition]);

  return menuPosition;
};
